import { supabase } from '@/core/database/supabase';

export interface ProjectEditForm {
  id: string;
  code: string;
  name: string;
  clientName: string;
  location: string;
  projectType: string;
  statusCode: string;
  startDate: string;
  endDate: string;
  contractAmount: number;
  budgetAmount: number;
  description: string; // This is the RAW description without the metadata tag
  pmUserId: string;
  siteManagerUserId: string;
  // Metadata fields (stored inside description with [METADATA]: prefix)
  thumbnail: string;
  attachments: string[];
  teamIds: string[];
  managementUnit: string;
  duration: string;
  contractType: string;
  priority: string;
  note: string;
}

export const projectEditService = {
  async getProject(id: string): Promise<ProjectEditForm | null> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', id)
      .single();

    if (error || !data) return null;

    const rawDescription = data.description || '';
    const metaMatch = rawDescription.match(/\[METADATA\]: (\{.*\})/);
    let meta: any = {};
    let pureDescription = rawDescription;

    if (metaMatch) {
      try {
        meta = JSON.parse(metaMatch[1]);
        pureDescription = rawDescription.replace(/\[METADATA\]: \{.*\}/, '').trim();
      } catch (e) {
        console.error('Error parsing metadata from description:', e);
      }
    }

    return {
      id: data.id,
      code: data.code,
      name: data.name,
      clientName: data.client_name || '',
      location: data.location || '',
      projectType: data.project_type,
      statusCode: data.status_code,
      startDate: data.start_date || '',
      endDate: data.end_date || '',
      contractAmount: data.contract_amount || 0,
      budgetAmount: data.budget_amount || 0,
      description: pureDescription,
      pmUserId: data.pm_user_id || '',
      siteManagerUserId: data.site_manager_user_id || '',
      // Map from parsed metadata
      thumbnail: meta.thumbnail || '',
      attachments: meta.attachments || [],
      teamIds: meta.teamIds || [],
      managementUnit: meta.managementUnit || '',
      duration: meta.duration || '',
      contractType: meta.contractType || '',
      priority: meta.priority || 'medium',
      note: meta.note || '',
    };
  },

  async updateProject(form: ProjectEditForm): Promise<void> {
    const metadata = {
      thumbnail: form.thumbnail,
      attachments: form.attachments,
      teamIds: form.teamIds,
      managementUnit: form.managementUnit,
      duration: form.duration,
      contractType: form.contractType,
      priority: form.priority,
      note: form.note,
    };

    const finalDescription = `${form.description}\n\n[METADATA]: ${JSON.stringify(metadata)}`;

    const { error } = await supabase
      .from('projects')
      .update({
        code: form.code,
        name: form.name,
        client_name: form.clientName,
        location: form.location,
        project_type: form.projectType,
        status_code: form.statusCode,
        start_date: form.startDate || null,
        end_date: form.endDate || null,
        contract_amount: form.contract_amount,
        budget_amount: form.budget_amount,
        description: finalDescription, // Save metadata inside description
        pm_user_id: form.pmUserId || null,
        site_manager_user_id: form.siteManagerUserId || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', form.id);

    if (error) throw error;
  }
};
